<template>
    <v-dialog :value="value" persistent scrollable max-width="600px">
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{title}}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="$emit('input', false)">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="overlay-dialog-body">

                <v-alert
                        class="mb-2"
                        :value="show_alert"
                        type="error"
                        v-html="alert_messages.join('<br>')"
                ></v-alert>

                <slot></slot>

                <v-flex xs12 class="text-xs-center">
                    <v-text-field
                      class="mt-2"
                      :label="$t('upload.select_file')"
                      @click='pickFile'
                      prepend-icon='attach_file'
                      :value="n_selected"
                      readonly
                      :loading="uploading"
                    >
                        <v-progress-linear
                          v-if="uploading"
                          slot="progress"
                          :value="overallProgress"
                          :color="color"
                          height="7"
                        ></v-progress-linear>
                    </v-text-field>
                    <input
                      type="file"
                      style="display: none"
                      ref="fileInput"
                      :accept="accept"
                      :multiple="multiple"
                      required
                      @change="onFilePicked"
                    >
                    <!--<img :src="imageUrl" height="150" v-if="imageUrl"/>-->
                    <v-container fluid grid-list-sm>
                        <v-layout row wrap>
                            <v-flex v-for="(item, i) in items" :key="i" xs6 sm4>
                                <v-badge
                                    :color="getBadgeColor(item)"
                                    style="width: 100%;"
                                    class="upload-dialog-file-badge"
                                >
                                    <template>
                                        <v-icon
                                          v-if="!item.uploading && !item.uploaded"
                                          slot="badge"
                                          dark
                                          small
                                          @click="removeFile(item)"
                                        >
                                            close
                                        </v-icon>
                                        <span v-else-if="item.uploading">
                                            <v-progress-circular
                                                    color="green"
                                                    size="22"
                                                    :rotate="-90"
                                                    :value="item.percentUploaded"
                                            ></v-progress-circular>
                                        </span>
                                        <v-icon
                                          v-else-if="item.uploaded && item.success"
                                          slot="badge"
                                          dark
                                          small
                                        >
                                            check
                                        </v-icon>
                                        <v-icon
                                            v-else-if="item.uploaded && !item.success"
                                            slot="badge"
                                            dark
                                            small
                                        >
                                            close
                                        </v-icon>

                                    </template>
                                    <v-card>
                                        <v-img
                                            v-if="item.file.type.match(/image\/.*/)"
                                            :src="item.url"
                                            :alt="item.name"
                                            height="100"
                                            contain
                                        ></v-img>
                                        <video
                                            v-else-if="item.file.type === 'video/mp4'"
                                            height="100"
                                            width="100%"
                                            style="object-fit: contain;"
                                            muted autoplay playsinline loop
                                        >
                                            <source :src="item.url" type="video/mp4" />
                                        </video>
                                        <v-icon
                                            v-else
                                            size="95"
                                        >
                                            {{getIcon(item.file.name)}}
                                        </v-icon>
                                        <div class="text-xs-center text-truncate">{{item.name}}</div>
                                        <!--<div class="text-xs-center text-truncate">{{item.percentUploaded}}</div>-->
                                    </v-card>
                                </v-badge>
                            </v-flex>
                        </v-layout>
                    </v-container>
                </v-flex>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" flat @click="$emit('input', false)">{{$t('action.cancel')}}</v-btn>
                <v-btn color="blue darken-1" flat @click="submit" :loading="uploading">{{$t('action.submit')}}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import RequestHandler from '~/utils/request-handler'
    import {UrlServices} from '~/utils/url-manager'
    import axios from 'axios'
    import Utils from '~/utils';

    const CancelToken = axios.CancelToken;

    export default {
        props: {
            value: Boolean,         // Set true to show. Do NOT change this name so that we can use v-model in parent components
            title: String,          // Dialog title
            accept: String,         // Accepted file types, e.g. image/*
            multiple: Boolean,      // Allow multiple
            maxSizeInMB: Number,    // maximum size per file in bytes
            type: String,           // Meta data to submit with files
            bypassUpload: Boolean,  // Bypass the upload process and just return the selected file
            adminUpload: {
                type: Boolean,
                default: true
            },
            postFields: {
                type: Object,
                default: () => {}
            },
        },
        data () {
            return {
                show_alert: false,
                alert_messages: [],
                uploading: false,
                items: []   // item properties: name, url, file, percentUploaded, uploading
            }
        },
        computed: {
            n_selected: function () {
                return this.items.length ? this.$t('upload.n_files_selected', [this.items.length]) : '';
            },
            color () {
                return ['error', 'warning', 'success'][Math.floor(this.overallProgress / 40)];
            },
            overallProgress: function () {
                let percent = _.reduce(this.items, (memo, x) => {
                    return memo + x.percentUploaded;
                }, 0);
                return Math.round(percent / this.items.length);
            }
        },
        watch: {
            value: function(newValue) {
                if (!newValue) {
                    // Cancel all upload and clear data when dialog is closed
                    _.each(this.items, x => {
                        if (x.uploading && !x.uploaded && x.cancel) {
                            x.cancel();
                        }
                    });
                    this.items = [];
                    this.uploading = false;
                    this.$refs.fileInput.value = '';
                    this.show_alert = false;
                    this.alert_messages = [];
                }
            }
        },
        methods: {
            pickFile () {
                this.$refs.fileInput.click();
            },
            onFilePicked (e) {
                const files = e.target.files;
                if (files.length) {
                    if (!this.multiple) {
                        this.items = [];
                    }
                    _.each(files, x => {
                        let error = null;
                        if (_.some(this.items, i => {return i.name === x.name;})) {
                            error = this.$t('upload.file_selected');
                        } else if (x.name.lastIndexOf('.') <= 0) {  // make sure there is file name
                            error = this.$t('upload.file_name_empty');
                        } else if (x.size > this.maxSizeInMB * 1024 * 1024) {
                            error = this.$t('upload.file_size_exceeded', [this.maxSizeInMB]);
                        }
                        if (error !== null) {
                            getApp.snackbar = {
                                show: true,
                                color: 'red',
                                text: error,
                                timeout: 3000
                            };
                            return;
                        }

                        let fr = new FileReader();
                        fr.readAsDataURL(x);
                        fr.addEventListener('load', () => {
                            let item = {
                                name: x.name,
                                url: fr.result,
                                file: x,
                                percentUploaded: 0,
                                uploading: false,
                                uploaded: false
                            };
                            this.items.push(item);
                        });
                    });
                }
            },
            removeFile (item) {
                let i = _.findIndex(this.items, {name: item.name});
                this.items.splice(i, 1);
            },
            getIcon (name) {
                return Utils.getIconByFileName(name);
            },
            getBadgeColor (item) {
                if (item.percentUploaded === 0)
                    return 'grey';
                else if (item.percentUploaded >= 100){
                    if(item.success){
                        return 'green';
                    } else {
                        return 'red';
                    }
                }
                else
                    return 'transparent';
            },

            submit () {
                if (this.items.length > 0) {
                    this.uploading = true;
                    this.show_alert = false;
                    this.alert_messages = [];
                    if (this.bypassUpload) {
                        this.$emit('success', this.multiple ? this.items : this.items[0]);
                        //this.$emit('input', false);
                    } else {
                        let processed_item_length = 0;

                        _.each(this.items, x => {
                            let form = new FormData();
                            form.append('type', this.type);
                            form.append('file', x.file);

                            _.each(this.postFields, function(value, key){
                                form.append(key, value);
                            });

                            let config = {
                                headers: {
                                    'Content-Type': 'multipart/form-data'
                                },
                                cancelToken: new CancelToken(function executor(c) {
                                    x.cancel = c;
                                }),
                                onUploadProgress: function (progressEvent) {
                                    //console.log(progressEvent);
                                    x.percentUploaded = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                    x.uploaded = x.percentUploaded === 100;
                                }
                            };

                            x.uploading = true;
                            x.uploaded = false;
                            x.success = false;
                            x.percentUploaded = 0;

                            let me = this;
                            RequestHandler.Post(UrlServices.createUrl({'name': (me.adminUpload ? 'api_file_upload' : 'api_public_file_upload') }), form, {
                                'complete': function () {
                                    // console.log('complete');
                                    // me.uploading = !(_.every(me.items, i => {
                                    //     return i.uploading && i.uploaded;
                                    // }));
                                    processed_item_length++;

                                    if(processed_item_length == me.items.length){
                                        let has_error = false;
                                        let data = [];
                                        if(me.multiple) {
                                            _.each(me.items, function(item){
                                                if(item.result){
                                                    data.push(item);
                                                } else {
                                                    has_error = true;
                                                }
                                            });
                                            me.$emit('success', data);
                                            me.$emit('input', has_error);
                                        } else {
                                            data = me.items[0];

                                            if(data.result){
                                                me.$emit('success', data);
                                                me.$emit('input', has_error);
                                            }
                                        }

                                        me.uploading = false;
                                    }
                                    x.uploading = false;
                                },
                                'success': function (data) {
                                    // console.log('success', data);
                                    x.result = data;
                                    x.success = true;
                                },
                                'error': function (data) {

                                    me.show_alert = true;
                                    me.alert_messages.push(data.message + '(' + x.file.name + ')');

                                    // if (!me.uploading) {
                                    //     let data = [];
                                    //     if(me.multiple) {
                                    //         _.each(me.items, function(item){
                                    //             if(item.result){
                                    //                 data.push(item);
                                    //             }
                                    //         });
                                    //     } else {
                                    //         data = me.items[0];
                                    //     }
                                    //
                                    //     me.$emit('success', data);
                                    //     me.$emit('input', false);
                                    // }

                                    // getApp.snackbar = {
                                    //     show: true,
                                    //     color: 'error',
                                    //     text: data.message,
                                    //     timeout: 3000
                                    // };
                                }
                            }, config);
                        });
                    }
                }
            }
        }
    }
</script>

<style>
    .upload-dialog-file-badge > span {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
    }
</style>